import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Select } from "chakra-react-select";
import { Box, Grid, Input, FormControl, Text } from "@chakra-ui/react";

import PrimaryButton from "../../../components/PrimaryButton";
import PrimaryFormLabel from "../../../components/PrimaryFormLabel";
import {
  Days,
  genderData,
  stageMasterData,
  statusMasterData,
} from "../../common/constants/index";
import { filtersPayload } from "../../utils";
import FormSelector from "../../../components/FormSelector";

const Filters = ({ onSearch }) => {
  const [country, setCountry] = useState(null);
  const [healthConcern, setHealthConcern] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [stage, setStage] = useState(stageMasterData[0]);
  const [status, setStatus] = useState([]);
  const [gender, setGender] = useState(null);
  const [daysFilter, setDaysFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const { hospitalData, healthConcernData, countryData } = useSelector(
    (state) => state.ips
  );

  const handleStageChange = (selectedStage) => {
    setStage(selectedStage);
    setSelectedStatus(null);

    if (selectedStage) {
      const stageStatusOptions = statusMasterData[selectedStage.value];
      setStatus(stageStatusOptions || []);
    } else {
      setStatus([]);
    }
  };

  useEffect(() => {
    const today = new Date();
    const fifteenDaysAgo = new Date(today);
    fifteenDaysAgo.setDate(today.getDate() - 15);
    setFromDate(fifteenDaysAgo.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  }, [customDateRange]);

  useEffect(() => {
    const stageStatusOptions = statusMasterData[stageMasterData[0].value];
    setStatus(stageStatusOptions || []);
  }, []);

  const handleDaysFilterChange = (selectedOption) => {
    if (selectedOption) {
      setDaysFilter(selectedOption.value);
      if (selectedOption.value === "custom") {
        setCustomDateRange(true);
      } else {
        setCustomDateRange(false);
      }
    }
  };

  const handleFromDateChange = (e) => {
    const selectedFromDate = e.target.value;
    setFromDate(selectedFromDate);

    if (customDateRange && toDate < selectedFromDate) {
      setDateError("Please select a valid date range.");
    } else {
      setDateError("");
    }
  };

  const handleSearch = () => {
    if (dateError) {
      return;
    }
    const filters = filtersPayload(
      healthConcern,
      hospital,
      gender,
      country,
      daysFilter,
      fromDate,
      toDate,
      stage?.value,
      selectedStatus?.value
    );
    onSearch(filters);
  };

  return (
    <Box
      ml="10px"
      p={4}
      shadow="md"
      borderWidth="1px"
      borderRadius="md"
      bgColor={"#ffff"}
      flex={{ base: "100%", md: "calc(50% - 10px)" }}
    >
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
      >
        <FormSelector
          label={"Stage"}
          isMulti={false}
          placeholder="Select Stage"
          closeMenuOnSelect={true}
          options={stageMasterData}
          value={stage}
          onChange={handleStageChange}
          mr="20px"
        />
        <FormSelector
          label="Status"
          isMulti={false}
          placeholder={stage ? "Select Status" : "Please select a stage first"}
          closeMenuOnSelect={true}
          options={status}
          value={selectedStatus}
          onChange={(selectedOption) => setSelectedStatus(selectedOption)}
          isDisabled={!stage}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
      >
        <FormSelector
          label={"Hospital"}
          isMulti={true}
          placeholder="Select Hospital"
          closeMenuOnSelect={false}
          options={hospitalData}
          value={hospital}
          onChange={(selectedOption) => setHospital(selectedOption)}
          mr="20px"
        />
        <FormSelector
          label="Health Concern"
          isMulti={true}
          placeholder="Select Health Concern"
          closeMenuOnSelect={false}
          options={healthConcernData}
          value={healthConcern}
          onChange={(selectedOption) => setHealthConcern(selectedOption)}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
      >
        <FormSelector
          isMulti={true}
          placeholder="Select Country"
          label="Country"
          closeMenuOnSelect={false}
          options={countryData}
          value={country}
          onChange={(selectedOption) => setCountry(selectedOption)}
          mr="20px"
        />
        <FormSelector
          label="Gender"
          isMulti={true}
          placeholder="Select Gender"
          closeMenuOnSelect={false}
          options={genderData}
          value={gender}
          onChange={(selectedOption) => setGender(selectedOption)}
        />
      </Box>
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <FormControl>
          <PrimaryFormLabel>Days</PrimaryFormLabel>
          <Select
            value={Days.find((day) => day.value === daysFilter)}
            options={Days}
            onChange={(selectedOption) =>
              handleDaysFilterChange(selectedOption)
            }
          />
        </FormControl>

        {customDateRange && (
          <>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <FormControl>
                <PrimaryFormLabel>From Date</PrimaryFormLabel>
                <Input
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  value={fromDate}
                  onChange={handleFromDateChange}
                />
                {dateError && <Text color="red.500">{dateError}</Text>}
              </FormControl>

              <FormControl>
                <PrimaryFormLabel>To Date</PrimaryFormLabel>
                <Input
                  type="date"
                  min={fromDate}
                  max={new Date().toISOString().split("T")[0]}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "10px",
        }}
      >
        <PrimaryButton onClick={handleSearch}>Search</PrimaryButton>
      </div>
    </Box>
  );
};

export default Filters;
